import { EntityService } from "./entity.service";
import { EntityData } from "./entity.data";
import { ReflectiveInjector } from "@angular/core";
import { ReplaySubject } from "rxjs/ReplaySubject";
import { Subject } from "rxjs/Subject";
import { DataResourceModel } from "../data.resource.model";
/**
 * Provides an entity model and its dependencies
 * @param modelType
 * @returns {any[]}
 */
export function provideEntityModel(modelType) {
    return [
        modelType,
        { provide: EntityModel, useExisting: modelType },
        { provide: DataResourceModel, useExisting: modelType }
    ];
}
export var EntityModelStatus;
(function (EntityModelStatus) {
    EntityModelStatus[EntityModelStatus["EDIT"] = 0] = "EDIT";
    EntityModelStatus[EntityModelStatus["INSERT"] = 1] = "INSERT";
})(EntityModelStatus || (EntityModelStatus = {}));
/**
 * Base class for all entity models. A model represents the resource state, as well as provides operations
 * that can be performed on the resource.
 *
 * A default service implementation is provided for performing server side communication. More specific implementations
 * may be provided via the subclass constructor if required.
 *
 * Generic parameter T defines the type of EntityData to be held by the model.
 *
 * Created by Robert on 13/09/2016.
 */
export class EntityModel extends DataResourceModel {
    /**
     * Constructs with the service which is used for all server side comms.
     *
     * This constructor must be replicated in subclasses to allow Angular dependency injection to provide the service.
     * Subclasses may also specify a more specialised service that extends EntityService. If so, then the subclass
     * must also override #getEntityService
     *
     * @param injector Injector
     * @param dataType EntityData subclass
     * @param resourceVersion Resource version
     */
    constructor(injector, dataType) {
        super(dataType);
        this.injector = injector;
        /**
         * Can be subscribed to get the entity data when it is resolved
         * @type {ReplaySubject<any>}
         * @private
         */
        this._$data = new ReplaySubject(1);
        this.$data = this._$data.asObservable();
        this._$componentLoaded = {};
        this._$collectionLoaded = {};
        this._$collectionChanged = {};
        if (injector == null) {
            console.error("!!!! Injector not provided. This is most likely due to a missing constructor in " + this.constructor.toString());
        }
        this.createService();
        this.createEntity(false);
        this.pristineInstance = true;
    }
    getNameSpace() {
        return this.service.getNameSpace();
    }
    getResource() {
        return this.service.getResource();
    }
    /**
     * Initialises this EntityModel with the state from the server
     * @returns {Promise<any>} JSON object representation of the entity
     * @param identifier
     * @param params
     */
    loadEntity(identifier, params) {
        let p = this.service.getEntity(identifier, params);
        p.then(json => this.loadData(json)).catch((error) => {
        });
        return p;
    }
    cloneEntity(identifier) {
        let p = this.service.cloneEntity(identifier);
        p.then(json => {
            this.loadData(json, true);
            this.onCloneEntity(this.data);
        }).catch((error) => { });
        return p;
    }
    loadCollection(collectionPath, actualCollectionPath) {
        let p = this.service.getCollection(actualCollectionPath || collectionPath);
        p.then(json => this.data.loadCollection(collectionPath, json));
        return p;
    }
    loadComponent(componentPath, actualComponentPath) {
        let p = this.service.getComponent(actualComponentPath || componentPath);
        p.then(json => this.data.loadComponent(componentPath, json));
        return p;
    }
    newEntity() {
        this.status = EntityModelStatus.INSERT;
        if (this.pristineInstance) {
            this.onNewEntity(this.data);
            this._$data.next(this.data);
        }
        else {
            this.createEntity();
        }
        this.pristineInstance = false;
    }
    /**
     * Persist the state of this model to the server
     * @return (Promise<any>) JSON object containing validation errors
     */
    saveEntity() {
        this.onBeforeSaveEntity(this.data);
        return new Promise((resolve, reject) => {
            let save = this.status == EntityModelStatus.INSERT ? this.service.createEntity(this.data) : this.service.updateEntity(this.data);
            save.then(json => {
                this.loadData(json);
                resolve(undefined);
            })
                .catch((error) => {
                if (error.validationFailure) {
                    resolve(error.data);
                }
                else {
                    reject(error);
                }
            });
        });
    }
    deleteEntity(identifier) {
        return this.service.deleteEntity(identifier);
    }
    discontinueEntity(identifier) {
        return this.service.discontinueEntity(identifier);
    }
    recontinueEntity(identifier) {
        return this.service.recontinueEntity(identifier);
    }
    syncEntity(entitySyncRequest) {
        return this.service.syncEntity(entitySyncRequest);
    }
    /**
     * Helper method to allow framework components to check for discontinued entities.
     * The default behaviour is to return the value of a "discontinued" field on the EntityData object held by this model.
     * This method may be overridden by search model implementations, however the preferred approach is to provide an appropriate getter on the EntityData class itself if required.
     * @return true if the entity is discontinued, false if not, or undefined if the entity does not support discontinuation.
     */
    isEntityDiscontinued() {
        if (!this.data) {
            return undefined;
        }
        return this.data["discontinued"];
    }
    /**
     * Helper method to allow framework components to mark entities as discontinued.
     * The default behaviour is to set the value of a "discontinued" field on the EntityData object held by this model.
     * This method may be overridden by search model implementations, however the preferred approach is to provide an appropriate setter on the EntityData class itself if required.
     * @param discontinued
     */
    setEntityDiscontinued(discontinued) {
        if (this.data) {
            this.data["discontinued"] = discontinued;
        }
    }
    /**
     * Indicates that the authenticated user has permission to create the entity
     * @returns {Promise<boolean>}
     */
    canCreateEntity() {
        return this.service.canCreateEntity();
    }
    /**
     * Indicates that the authenticated user has permission to retrieve the entity
     * @returns {Promise<boolean>}
     */
    canGetEntity() {
        return this.service.canGetEntity();
    }
    /**
     * Indicates that the authenticated user has permission to update the entity
     * @returns {Promise<boolean>}
     */
    canUpdateEntity() {
        return this.service.canUpdateEntity();
    }
    /**
     * Indicates that the authenticated user has permission to delete the entity
     * @returns {Promise<boolean>}
     */
    canDeleteEntity() {
        return this.service.canDeleteEntity();
    }
    /**
     * Indicates that the authenticated user has permission to discontinue/recontinue the entity
     * @returns {Promise<boolean>}
     */
    canDiscontinueEntity() {
        return this.service.canDiscontinueEntity();
    }
    /**
     * Indicates that the authenticated user has permission to sync the entity to another instance in the group
     * @returns {Promise<boolean>}
     */
    canSyncEntity() {
        return this.service.canSyncEntity();
    }
    /**
     * Returns an observable that resolves when the specified component is loaded. It will always return the last value for the component.
     * @returns {Observable<EntityData<any>>}
     */
    $componentLoaded(componentPath) {
        if (!this._$componentLoaded[componentPath]) {
            this._$componentLoaded[componentPath] = new ReplaySubject(1);
        }
        return this._$componentLoaded[componentPath].asObservable();
    }
    /**
     * Returns an observable that resolves when the specified collection is assigned or loaded. It will always return the last value for the array.
     * @returns {Observable<EntityData<any>[]>}
     */
    $collectionLoaded(collectionPath) {
        if (!this._$collectionLoaded[collectionPath]) {
            this._$collectionLoaded[collectionPath] = new ReplaySubject(1);
        }
        return this._$collectionLoaded[collectionPath].asObservable();
    }
    /**
     * Returns an observable that emits when the specified collection changes including assigning a new value or adding and removing items from the collection.
     * @returns {Observable<EntityData<any>[]>}
     */
    $collectionChanged(collectionPath) {
        if (!this._$collectionChanged[collectionPath]) {
            this._$collectionChanged[collectionPath] = new Subject();
        }
        return this._$collectionChanged[collectionPath].asObservable();
    }
    /**
     * Can be overridden to set default values when adding a new entity
     * @param {EntityData<T extends EntityData<T>>} entityData
     */
    onNewEntity(entityData) {
    }
    /**
     * Can be overridden to set default values when cloning an entity
     * @param {EntityData<T extends EntityData<T>>} entityData
     */
    onCloneEntity(entityData) {
    }
    /**
     * Returns the service type expected by this model (defaults to a generic EntityService).
     * Extending models should override this if a specialised service is required.
     * @returns {EntityService}
     */
    getEntityService() {
        return EntityService;
    }
    /**
     * Can be overridden to change the entity before saving
     * @param {T} entityData
     */
    onBeforeSaveEntity(entityData) {
    }
    /**
     * Can be overridden to change the entity after loading
     * @param {T} entityData
     */
    onAfterLoadEntity(entityData) {
    }
    createService() {
        const reflectInjector = ReflectiveInjector.resolveAndCreate([
            { provide: EntityService.ENTITY_RESOURCE, useValue: this.getResourceName() },
            { provide: EntityService.RESOURCE_VERSION, useValue: this.getResourceVersion() },
            this.getEntityService()
        ], this.injector);
        this.service = reflectInjector.get(this.getEntityService());
    }
    /**
     * Create a new instance of the entity, and subscribe to the resolutions events.
     */
    createEntity(triggerDataChange = true) {
        if (this.collectionResolutionsSubscription) {
            this.collectionResolutionsSubscription.unsubscribe();
        }
        if (this.componentResolutionsSubscription) {
            this.componentResolutionsSubscription.unsubscribe();
        }
        if (this.componentLoadedSubscription) {
            this.componentLoadedSubscription.unsubscribe();
        }
        if (this.collectionLoadedSubscription) {
            this.collectionLoadedSubscription.unsubscribe();
        }
        if (this.collectionChangedSubscription) {
            this.collectionChangedSubscription.unsubscribe();
        }
        let entityData = EntityData.create(this.dataType);
        this.collectionResolutionsSubscription = entityData.collectionResolutions.subscribe((request) => {
            this.loadCollection(request.path, request.actualPath);
        });
        this.componentResolutionsSubscription = entityData.componentResolutions.subscribe((request) => {
            this.loadComponent(request.path, request.actualPath);
        });
        this.componentLoadedSubscription = entityData.componentLoaded.subscribe((loaded) => {
            if (!this._$componentLoaded[loaded.path]) {
                this._$componentLoaded[loaded.path] = new ReplaySubject(1);
            }
            this._$componentLoaded[loaded.path].next(loaded.component);
        });
        this.collectionLoadedSubscription = entityData.collectionLoaded.subscribe((loaded) => {
            if (!this._$collectionLoaded[loaded.path]) {
                this._$collectionLoaded[loaded.path] = new ReplaySubject(1);
            }
            this._$collectionLoaded[loaded.path].next(loaded.collection);
        });
        this.collectionChangedSubscription = entityData.collectionChanged.subscribe((changed) => {
            let subject = this._$collectionChanged[changed.path];
            if (subject) {
                subject.next(changed.collection);
            }
        });
        this.data = entityData;
        if (triggerDataChange) {
            this.onNewEntity(this.data);
            this._$data.next(this.data);
        }
    }
    /**
     * Load the json to the data and set the status to EDIT
     * @param json
     */
    loadData(json, cloning = false) {
        if (!this.pristineInstance || cloning) {
            this.createEntity(false);
        }
        this.pristineInstance = false;
        if (this.getSerializeToEntityData()) {
            this.data.load(json);
        }
        else {
            this.data = json;
        }
        this.status = cloning ? EntityModelStatus.INSERT : EntityModelStatus.EDIT;
        this.onAfterLoadEntity(this.data);
        if (cloning) {
            this.canCreateEntity().then((value) => this.readonly = !value);
        }
        else {
            this.canUpdateEntity().then((value) => this.readonly = !value);
        }
        this._$data.next(this.data);
    }
    isEntityCloneable() {
        return true;
    }
    notifyDataChange() {
        this._$data.next(this.data);
    }
    getSerializeToEntityData() {
        return true;
    }
}
//# sourceMappingURL=entity.model.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/resource/entity/entity.model.js
// module id = 146
// module chunks = 1